import React, { useState, useEffect } from 'react'
import convert from 'htmr'
import cn from 'classnames'
import { kcalToKJ } from '../../metricsConverter'
import { localizeLink } from '../../utils'
import { trackStartRegistration } from '../../analytics'
import useLocale from '../../hooks/useLocale'

type Exercises = Array<{ exercise: string; calories: number }>

function CalorieBurnCalculator() {
  const { language } = useLocale()
  const [system, setSystem] = useState('kcal')
  const [time, setTime] = useState(30)
  const [searchString, setSearchString] = useState('')
  const [hits, setHits] = useState<Exercises | false>(false)
  const [exercises, setExercises] = useState<Exercises | false>(false)

  useEffect(() => {
    import(/* webpackChunkName: "Exercises" */ 'media/exercises.json').then(
      ({ default: exercises }) => {
        setExercises(exercises)
      },
    )
  }, [])

  const handleSystem = (system) => () => {
    setSystem(system)
  }

  const calculateEnergy = (calories) =>
    Math.round((system === 'kj' ? kcalToKJ(calories) : calories) * time)

  const handleSearchString = (event) => {
    setSearchString(event.target.value)
  }

  const handleTime = (event) => {
    setTime(Number(event.target.value) || 30)
  }

  useEffect(() => {
    if (searchString.length < 2) {
      return setHits(false)
    }

    if (!exercises) {
      return
    }

    const newHits: Exercises = []
    for (let i = 0; i < exercises.length; i++) {
      if (
        exercises[i].exercise
          .toLowerCase()
          .indexOf(searchString.toLowerCase()) > -1 ||
        exercises[i].exercise
          .toLowerCase()
          .indexOf(searchString.replace(' ', '-').toLowerCase()) > -1
      ) {
        newHits.push({ ...exercises[i] })
      }
    }

    // Highlight case-insensitively
    for (let j = 0; j < newHits.length; j++) {
      newHits[j].exercise = newHits[j].exercise.replace(
        new RegExp(searchString, 'gi'),
        (str) => `<b>${str}</b>`,
      )
    }

    newHits.sort(
      (a, b) =>
        a.exercise.indexOf(searchString) - b.exercise.indexOf(searchString),
    )

    setHits(newHits)
    // FIXME: dont ignore
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchString])

  return (
    <div className="c-calculator  c-cal-burn" data-uid="CalorieBurnCalculator">
      <h2 className="c-calculator__heading">Calories Burned Calculator</h2>
      <div className="c-calculator__inner">
        <form action="/">
          <div className="c-calculator__switcher">
            <button
              type="button"
              className={cn('c-calculator__switcher-button', {
                'c-calculator__switcher-button--active': system === 'kj',
              })}
              onClick={handleSystem('kj')}
            >
              kJ
            </button>
            <button
              type="button"
              className={cn('c-calculator__switcher-button', {
                'c-calculator__switcher-button--active': system === 'kcal',
              })}
              onClick={handleSystem('kcal')}
            >
              kcal
            </button>
          </div>
          <div className="c-cal-burn__inputs">
            <div className="c-cal-burn__input c-cal-burn__input--long">
              <label htmlFor="activity">Activity</label>
              <input
                id="activity"
                onChange={handleSearchString}
                value={searchString}
                type="search"
                placeholder="E.g. tennis or crossfit"
              />
            </div>
            <div className="c-cal-burn__input c-cal-burn__input--short">
              <label htmlFor="time">Minutes</label>
              <input
                id="time"
                onChange={handleTime}
                value={time}
                type="number"
                step="1"
              />
            </div>
          </div>
          {hits && hits.length > 0 && (
            <div>
              <table className="c-cal-burn__hits">
                <thead>
                  <tr>
                    <th scope="col">Activity</th>
                    <th scope="col">
                      {system}/{time} min
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {hits.map((hit) => (
                    <tr key={hit.exercise}>
                      <td>{convert(hit.exercise)}</td>
                      <td>{calculateEnergy(hit.calories)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="c-cal-burn__cta">
                <p>Track your burned calories with our app:</p>
                <a
                  className="c-button c-button--primary"
                  href={localizeLink('/signup/', language)}
                  onClick={() => trackStartRegistration()}
                >
                  <span>Sign up for Lifesum</span>
                </a>
              </div>
            </div>
          )}
          {hits && !hits.length && (
            <p className="c-cal-burn__no-hits">
              Sorry, no activity found for &quot;{searchString}
              &quot;.
            </p>
          )}
        </form>
      </div>
    </div>
  )
}

export default CalorieBurnCalculator
